import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";

export type TriggerHubTab = "plannr-triggers" | "curo-triggers" | "email-triggers";

const TRIGGER_HUB_TABS = new Set<TriggerHubTab>([
  "plannr-triggers",
  "curo-triggers",
  "email-triggers",
]);

export type TriggersLocation = {
  tab: TriggerHubTab;
  event?: string;
  focus?: string;
};

function defaults(): TriggersLocation {
  return { tab: "plannr-triggers" };
}

function parseTab(raw: string | null | undefined): TriggerHubTab | null {
  if (raw && TRIGGER_HUB_TABS.has(raw as TriggerHubTab)) {
    return raw as TriggerHubTab;
  }
  return null;
}

function normalize(loc: TriggersLocation): TriggersLocation {
  const next: TriggersLocation = { tab: loc.tab };
  if (loc.event) next.event = loc.event;
  if (loc.focus) next.focus = loc.focus;
  return next;
}

function fromParams(params: Record<string, string | undefined>): TriggersLocation {
  const base = defaults();
  const tab = parseTab(params[NAV_KEYS.tab]);
  if (tab) base.tab = tab;
  const event = params[NAV_KEYS.event]?.trim();
  if (event) base.event = event;
  const focus = params[NAV_KEYS.focus]?.trim();
  if (focus) base.focus = focus;
  return normalize(base);
}

function fromUrl(_pathname: string, searchParams: URLSearchParams): TriggersLocation {
  return fromParams(Object.fromEntries(searchParams));
}

function toParams(loc: TriggersLocation): Record<string, string> {
  const n = normalize(loc);
  const d = defaults();
  const out: Record<string, string> = {};
  if (n.tab !== d.tab) out[NAV_KEYS.tab] = n.tab;
  if (n.event) out[NAV_KEYS.event] = n.event;
  if (n.focus) out[NAV_KEYS.focus] = n.focus;
  return out;
}

function toUrl(loc: TriggersLocation): string {
  const qs = new URLSearchParams(toParams(loc)).toString();
  return `/ops/triggers${qs ? `?${qs}` : ""}`;
}

export const triggersCodec: AppLocationCodec<TriggersLocation> = {
  appId: "triggers",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function buildTriggersWindowUrl(loc: TriggersLocation): string {
  return triggersCodec.toUrl(loc);
}
